"use client";

import { AdminLoader } from "@/components/admin/AdminLoader";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "@/i18n/navigation";
import { useEffect } from "react";

/**
 * Gate de las rutas privadas (FEATURE-029): **solo** verifica sesión Cognito y
 * redirige a login si falta. Sin chrome visual (cada zona privada trae el suyo;
 * hoy la única es el admin `/app`, que monta su propia consola).
 *
 * Bypass de desarrollo (FEATURE-014): con `NEXT_PUBLIC_DEV_BYPASS_TOKEN` activo se
 * saltea el gate (en local no hay Cognito real). Nunca en producción.
 *
 * La espera usa `AdminLoader` —el mismo loader, el mismo modo y el MISMO mensaje que el
 * boot de la consola (FEATURE-113)— aunque este gate sea genérico y aquel sea del admin.
 * Son dos montajes distintos que el usuario ve como una sola pantalla: si difirieran en
 * copy o en tema, el paso de uno a otro se vería como un salto. Hoy la única zona privada
 * es la consola; si aparece otra, el loader se sube a `components/ui/`.
 */
export default function PrivateLayoutClient({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, user } = useAuth();
  const router = useRouter();
  const devBypass = !!process.env.NEXT_PUBLIC_DEV_BYPASS_TOKEN;

  useEffect(() => {
    if (devBypass) return;
    if (!router || loading) return;
    if (!isAuthenticated()) router.replace("/login");
  }, [router, loading, isAuthenticated, devBypass]);

  if (loading && !devBypass) return <AdminLoader />;
  if (!user && !devBypass) return null;

  return <>{children}</>;
}
